import React from 'react';
import {
  Alert,
  Collapsible,
  DataTable,
  Button,
  Form,
  InputGroup,
  Pagination,
} from '@openedx/paragon';
import { useDispatch, useSelector } from 'react-redux';
import { RequestStatus } from '../../../../data/constants';
import {
  getCompanyEmployees,
  getCompanyEmployeeCount,
  getCompanyEmployeePagesForOrg,
  getCompanyEmployeesForOrg,
  getLearnerEnrollmentsForOrg,
} from '../../../data/selectors';
import {
  fetchAllLearnerEnrollmentsInBackground,
  fetchCompanyEmployees,
  fetchLearnerEnrollmentProgress,
} from '../../../data/thunks';
import { getEnterpriseMemberships } from '../../../data/api';
import { logStudioHomeError } from '../../../logging';

interface LearnerManagementTableProps {
  companyName: string | null;
  isLoading?: boolean;
}

interface CourseCertificate {
  status: string | null;
  link: string | null;
  grade: string | null;
}

interface CourseGrade {
  score: number;
  score_percent: number;
  source: string;
}

interface CourseProgress {
  completion_percentage: number;
  completed: boolean;
  started: boolean;
}

interface CourseEnrollment {
  course_id: string;
  display_name_with_default?: string;
  certificate?: CourseCertificate | null;
  grade?: CourseGrade | null;
  progress?: CourseProgress | null;
  last_active?: string | null;
}

// Top-level learner status:
// - unverified: FERPA/release-of-information not yet acknowledged
// - admin:      FERPA acknowledged and the learner is an org admin
// - assigned:   FERPA acknowledged, not an admin, has at least one course enrollment
// - verified:   FERPA acknowledged, not an admin, no enrollments yet
type LearnerStatus = 'unverified' | 'verified' | 'admin' | 'assigned' | 'pending';

interface LearnerRow {
  username: string;
  name: string;
  email: string;
  isAdmin: boolean;
  isPrimaryAdmin: boolean;
  ferpaAcknowledged: boolean;
  ferpaAcknowledgedTimestamp: string | null;
  lastActive: string | null;
  courses: CourseEnrollment[] | null; // null until this learner's enrollment batch resolves
  enrollmentLoadingStatus: string;
}

const DEFAULT_PAGE_SIZE = 10;
const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'All', label: 'All' },
  { value: 'unverified', label: 'Unverified' },
  { value: 'verified', label: 'Verified' },
  { value: 'admin', label: 'Admin' },
  { value: 'assigned', label: 'Assigned' },
];

const getLearnerStatus = (row: LearnerRow): LearnerStatus => {
  if (row.enrollmentLoadingStatus !== RequestStatus.SUCCESSFUL) {
    return 'pending';
  }
  if (!row.ferpaAcknowledged) {
    return 'unverified';
  }
  if (row.isAdmin) {
    return 'admin';
  }
  if ((row.courses?.length || 0) > 0) {
    return 'assigned';
  }
  return 'verified';
};

const STATUS_LABELS: Record<LearnerStatus, string> = {
  unverified: 'Unverified',
  verified: 'Verified',
  admin: 'Admin',
  assigned: 'Assigned',
  pending: 'Loading...',
};

const ExpandRowCell = ({ row }: { row: { original: LearnerRow } }) => (
  (row.original.courses?.length || 0) > 0 ? <DataTable.ExpandRow row={row} /> : null
);

const getCourseStatus = (progress?: CourseProgress | null) => {
  if (!progress || !progress.started) {
    return 'Not started';
  }
  if (!progress.completed) {
    return 'In progress';
  }
  return 'Completed';
};

const buildRoster = (employees: any[]): LearnerRow[] => {
  const seen = new Set<string>();
  return (Array.isArray(employees) ? employees : [])
    .filter((row) => {
      if (!row.username || seen.has(row.username)) {
        return false;
      }
      seen.add(row.username);
      return true;
    })
    .map((row) => ({
      username: row.username,
      name: row.name || '',
      email: row.email || '',
      isAdmin: row.is_admin === true,
      isPrimaryAdmin: row.is_primary_admin === true,
      ferpaAcknowledged: Boolean(row.ferpa_accepted ?? row.ferpa?.acknowledged),
      ferpaAcknowledgedTimestamp: row.accepted_date ?? row.ferpa?.acknowledged_timestamp ?? null,
      lastActive: null,
      courses: null,
      enrollmentLoadingStatus: RequestStatus.CLEAR,
    }));
};

const getLastActive = (courses: CourseEnrollment[] | null) => (
  (courses || [])
    .map((course) => course.last_active)
    .filter((value): value is string => Boolean(value))
    .sort()
    .pop() || null
);

const formatLastActive = (value: string | null) => (
  value ? new Date(value).toLocaleDateString() : '—'
);

const LearnerEnrollmentDetails = ({
  companyName,
  learner,
}: {
  companyName: string | null;
  learner: LearnerRow;
}) => {
  const dispatch = useDispatch();
  const learnerEnrollments = useSelector(
    (state: any) => getLearnerEnrollmentsForOrg(state, companyName),
  );
  const enrollmentEntry = learnerEnrollments[learner.username];
  const courses = enrollmentEntry?.courses || [];
  const progressLoadingStatus = enrollmentEntry?.progressLoadingStatus ?? RequestStatus.CLEAR;

  React.useEffect(() => {
    if (
      companyName
      && courses.length > 0
      && progressLoadingStatus === RequestStatus.CLEAR
    ) {
      dispatch(fetchLearnerEnrollmentProgress(companyName, learner) as any);
    }
  }, [companyName, courses.length, dispatch, learner, progressLoadingStatus]);

  const courseRows = courses.map((course) => ({
    course_id: course.course_id,
    display_name: course.display_name_with_default || course.course_id,
    status: progressLoadingStatus === RequestStatus.IN_PROGRESS ? 'Loading...' : getCourseStatus(course.progress),
    certificate_status: course.certificate?.status || 'Not issued',
    grade: course.grade?.score_percent != null ? `${course.grade.score_percent}%` : '—',
    progress: course.progress?.completion_percentage != null
      ? `${course.progress.completion_percentage}%`
      : '—',
  }));

  return (
    <div style={{
      padding: '1rem', width: '100%', maxWidth: '100%', overflowX: 'auto',
    }}
    >
      <DataTable
        initialState={{ pageSize: 5 }}
        initialTableOptions={{ autoResetPage: false }}
        data={courseRows}
        columns={[
          { Header: 'Course ID', accessor: 'course_id' },
          { Header: 'Course Name', accessor: 'display_name' },
          { Header: 'Status', accessor: 'status' },
          { Header: 'Certificate', accessor: 'certificate_status' },
          { Header: 'Grade', accessor: 'grade' },
          { Header: 'Progress', accessor: 'progress' },
        ]}
        itemCount={courseRows.length}
        isFilterable={false}
        isPaginated
      >
        <DataTable.Table />
        <DataTable.TableFooter />
      </DataTable>
    </div>
  );
};

const LearnerManagementTable: React.FC<LearnerManagementTableProps> = ({
  companyName,
  isLoading = false,
}) => {
  const dispatch = useDispatch();
  const legacyCompanyEmployees = useSelector(getCompanyEmployees);
  const employeePages = useSelector((state: any) => getCompanyEmployeePagesForOrg(state, companyName));
  const companyEmployees = useSelector((state: any) => getCompanyEmployeesForOrg(state, companyName));
  const legacyEmployeeCount = useSelector(getCompanyEmployeeCount);
  const employeeCount = employeePages?.count || legacyEmployeeCount;
  const learnerEnrollments = useSelector(
    (state: any) => getLearnerEnrollmentsForOrg(state, companyName),
  );

  const [currentPage, setCurrentPage] = React.useState(1);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [submittedEmail, setSubmittedEmail] = React.useState('');
  const [membershipLearner, setMembershipLearner] = React.useState<any>(null);
  const [selectedStatus, setSelectedStatus] = React.useState('All');
  const [showFilters, setShowFilters] = React.useState(false);
  const [pageInputValue, setPageInputValue] = React.useState('1');

  const rowsPerPage = DEFAULT_PAGE_SIZE;

  const roster = React.useMemo(
    () => buildRoster((companyEmployees.length ? companyEmployees : legacyCompanyEmployees) as any[]),
    [companyEmployees, legacyCompanyEmployees],
  );
  const currentPageUsernames = React.useMemo(
    () => new Set((employeePages
      ? employeePages.pages?.[currentPage]?.results || []
      : legacyCompanyEmployees).map(
      (employee) => employee.username,
    )),
    [currentPage, employeePages, legacyCompanyEmployees],
  );

  // Merge in whatever enrollment enrichment has resolved so far for this org's roster.
  const data = React.useMemo<LearnerRow[]>(() => roster.map((learner) => {
    const enrollmentEntry = learnerEnrollments[learner.username];
    return {
      ...learner,
      courses: enrollmentEntry?.courses ?? null,
      enrollmentLoadingStatus: enrollmentEntry?.loadingStatus ?? RequestStatus.CLEAR,
      lastActive: getLastActive(enrollmentEntry?.courses ?? null),
    };
  }), [roster, learnerEnrollments]);

  const displayedData = React.useMemo(() => {
    if (!membershipLearner || data.some((learner) => learner.email === membershipLearner.email)) {
      return data;
    }
    const enrollmentEntry = learnerEnrollments[membershipLearner.username];
    return [...data, {
      ...membershipLearner,
      courses: enrollmentEntry?.courses ?? null,
      enrollmentLoadingStatus: enrollmentEntry?.loadingStatus ?? RequestStatus.CLEAR,
      lastActive: getLastActive(enrollmentEntry?.courses ?? null),
    }];
  }, [data, learnerEnrollments, membershipLearner]);
  const hasLoadedSubmittedEmail = data.some(
    (learner) => learner.email.toLowerCase() === submittedEmail.trim().toLowerCase(),
  );

  const isFilteredView = selectedStatus !== 'All';

  const matchesFilters = React.useCallback((learner: LearnerRow) => {
    if (isFilteredView && getLearnerStatus(learner) !== selectedStatus) {
      return false;
    }
    return true;
  }, [isFilteredView, selectedStatus]);

  const filteredData = React.useMemo(() => {
    let result = displayedData;
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter((learner) => {
        const haystack = `${learner.name} ${learner.email}`.toLowerCase();
        return haystack.includes(query);
      });
    }
    return result.filter(matchesFilters);
  }, [displayedData, matchesFilters, searchQuery]);

  const effectivePageCount = Math.max(
    1,
    Math.ceil(Math.max(isFilteredView || searchQuery.trim() ? filteredData.length : employeeCount, 1) / rowsPerPage),
  );

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, companyName, selectedStatus]);

  React.useEffect(() => {
    setPageInputValue(String(currentPage));
  }, [currentPage]);

  React.useEffect(() => {
    if (currentPage > effectivePageCount) {
      setCurrentPage(effectivePageCount);
    }
  }, [currentPage, effectivePageCount]);

  const startIndex = (currentPage - 1) * rowsPerPage;
  const pageRows = React.useMemo(
    () => (isFilteredView || searchQuery.trim()
      ? filteredData.slice(startIndex, startIndex + rowsPerPage)
      : filteredData.filter((learner) => currentPageUsernames.has(learner.username))),
    [currentPageUsernames, filteredData, isFilteredView, searchQuery, startIndex, rowsPerPage],
  );

  // Retain support for callers that seed a legacy, unpaged roster (including existing tests).
  React.useEffect(() => {
    if (!companyName || isLoading || employeeCount || roster.length === 0) {
      return;
    }
    dispatch(fetchAllLearnerEnrollmentsInBackground(companyName, roster) as any);
  }, [companyName, dispatch, employeeCount, isLoading, roster]);

  React.useEffect(() => {
    if (
      !companyName
      || isLoading
      || employeePages?.pages?.[currentPage]
      || (!employeePages && legacyCompanyEmployees.length > 0)
    ) {
      return;
    }
    dispatch(fetchCompanyEmployees(companyName, currentPage, rowsPerPage) as any);
  }, [
    companyName,
    currentPage,
    dispatch,
    employeePages,
    isLoading,
    legacyCompanyEmployees.length,
    rowsPerPage,
  ]);

  React.useEffect(() => {
    const email = submittedEmail.trim().toLowerCase();
    if (!companyName || !email || !/^\S+@\S+\.\S+$/.test(email) || hasLoadedSubmittedEmail) {
      setMembershipLearner(null);
      return undefined;
    }

    let cancelled = false;
    getEnterpriseMemberships(email)
      .then((memberships) => {
        const membership = memberships.find((entry) => entry.company_name === companyName);
        if (!cancelled && membership) {
          const username = membership.username || email;
          setMembershipLearner({
            username,
            name: membership.name || email,
            email,
            isAdmin: membership.is_admin === true,
            isPrimaryAdmin: membership.is_primary_admin === true,
            ferpaAcknowledged: Boolean(membership.ferpa_accepted),
            ferpaAcknowledgedTimestamp: membership.accepted_date ?? null,
            lastActive: null,
          });
          dispatch(fetchAllLearnerEnrollmentsInBackground(companyName, [{ username, email }]) as any);
        }
      })
      .catch((error) => logStudioHomeError('cmp_enterprise_membership_lookup_failed', error));
    return () => { cancelled = true; };
  }, [companyName, dispatch, hasLoadedSubmittedEmail, submittedEmail]);

  const clearFilters = () => {
    setSelectedStatus('All');
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSubmittedEmail('');
    setMembershipLearner(null);
  };

  const handlePageSelect = (pageNumber: number) => {
    const nextPage = Math.min(Math.max(1, pageNumber), effectivePageCount);
    setCurrentPage(nextPage);
  };

  const handlePageInputSubmit = () => {
    const requestedPage = Number(pageInputValue);
    if (Number.isInteger(requestedPage) && requestedPage > 0) {
      handlePageSelect(requestedPage);
    } else {
      setPageInputValue(String(currentPage));
    }
  };

  const shouldRowBeExpandable = React.useCallback((row: any) => {
    const learner: LearnerRow = row.original;
    return (learner.courses?.length || 0) > 0;
  }, []);

  const expandedRowSubComponent = React.useCallback(({ row }) => (
    <LearnerEnrollmentDetails companyName={companyName} learner={row.original} />
  ), [companyName]);

  const tableInitialState = React.useMemo(() => ({
    sortBy: [{ id: 'name', desc: false }],
  }), []);
  const tableInitialTableOptions = React.useMemo(() => ({
    autoResetExpanded: false,
  }), []);

  const tableColumns = React.useMemo(() => [
    {
      id: 'expander',
      Header: '',
      Cell: ExpandRowCell,
      width: 60,
    },
    {
      Header: 'Name',
      id: 'name',
      accessor: 'name',
      minWidth: 120,
    },
    {
      Header: 'Email',
      accessor: 'email',
      minWidth: 200,
    },
    {
      Header: 'Last Active',
      accessor: (row: LearnerRow) => formatLastActive(row.lastActive),
      minWidth: 120,
    },
    {
      Header: 'Status',
      id: 'status',
      accessor: (row: LearnerRow) => STATUS_LABELS[getLearnerStatus(row)],
      minWidth: 100,
    },
    {
      Header: 'Release of Information Acknowledged',
      accessor: 'ferpaAcknowledgedTimestamp',
      minWidth: 200,
    },
  ], [shouldRowBeExpandable]);

  return (
    <div className="learner-management-table-wrapper">
      <InputGroup className="mb-3">
        <Form.Control
          type="text"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(event) => {
            setSearchQuery(event.target.value);
            if (!event.target.value.trim()) {
              setSubmittedEmail('');
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              setSubmittedEmail(searchQuery);
            }
          }}
        />
        {searchQuery && (
          <InputGroup.Append>
            <Button
              type="button"
              variant="tertiary"
              aria-label="Clear learner search"
              title="Clear search"
              onClick={clearSearch}
            >
              ×
            </Button>
          </InputGroup.Append>
        )}
      </InputGroup>
      {searchQuery.trim() && filteredData.length === 0 && companyEmployees.length < employeeCount && (
        <Alert variant="info" className="mb-3">
          Learner list is still loading. Try searching by the learner&apos;s email address and press Enter.
        </Alert>
      )}
      <div className="mb-3">
        <Collapsible
          open={showFilters}
          onToggle={setShowFilters}
          title={<h5 className="mb-0">Filters</h5>}
        >
          <div className="p-3 border rounded bg-light-100 mt-2">
            <div className="row">
              <div className="col-md-3 mb-3">
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Control
                    as="select"
                    value={selectedStatus}
                    onChange={(event) => setSelectedStatus(event.target.value)}
                  >
                    {STATUS_FILTER_OPTIONS.map((status) => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <Button variant="outline-secondary" onClick={clearFilters}>Clear filters</Button>
            </div>
          </div>
        </Collapsible>
      </div>
      <DataTable
        isLoading={isLoading}
        initialState={tableInitialState}
        initialTableOptions={tableInitialTableOptions}
        isSortable
        isExpandable
        data={pageRows}
        renderRowSubComponent={expandedRowSubComponent}
        columns={tableColumns}
      >
        <DataTable.Table />
        <DataTable.EmptyTable content="No results found" />
      </DataTable>
      {effectivePageCount > 1 && (
        <div className="d-flex flex-wrap align-items-center justify-content-between mt-3 gap-3">
          <div className="text-muted small">
            Page {currentPage} of {effectivePageCount}
          </div>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <Pagination
              size="small"
              currentPage={currentPage}
              pageCount={effectivePageCount}
              paginationLabel="learner table pagination"
              onPageSelect={handlePageSelect}
            />
            <div className="d-flex align-items-center gap-2">
              <Form.Label className="mb-0" htmlFor="learner-table-page-input">Go to page&nbsp;</Form.Label>
              <Form.Control
                id="learner-table-page-input"
                type="number"
                min={1}
                max={effectivePageCount}
                value={pageInputValue}
                onChange={(event) => setPageInputValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handlePageInputSubmit();
                  }
                }}
                style={{ width: '6rem' }}
              />
              <Button variant="outline-primary" size="sm" onClick={handlePageInputSubmit}>
                Go
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LearnerManagementTable;
